import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserCircle, FaIdBadge, FaMapMarkerAlt, FaBuilding, FaCheckCircle, FaTimesCircle, FaEdit, FaSave, FaTimes } from "react-icons/fa";

export default function Profile() {
  const [user, setUser] = useState(null);
  const [panchayats, setPanchayats] = useState([]);
  const [blockName, setBlockName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const navigate = useNavigate();

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          navigate("/");
          return;
        }

        // Fetch user info
        const resUser = await fetch("https://perambalur-swm-api.circularctech.com/api/Auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!resUser.ok) throw new Error("Failed to fetch user info");

        const userData = await resUser.json();
        setUser(userData);
        setEditedUser(userData);

        // Fetch panchayats for user's block
        if (userData.blockId) {
          const resPanchayats = await fetch(
            `https://perambalur-swm-api.circularctech.com/api/Auth/panchayats?blockId=${userData.blockId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (!resPanchayats.ok) throw new Error("Failed to fetch panchayats");

          const panchayatData = await resPanchayats.json();
          const data = panchayatData.data || [];
          setPanchayats(data);

          if (data.length > 0) setBlockName(data[0].blockName.trim());
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token, navigate]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedUser(user);
  };

  const handleSave = async () => {
    // Here you would typically make an API call to update the user
    // For now, we'll just update the local state
    setUser(editedUser);
    setIsEditing(false);
    // TODO: Implement actual API call to update user profile
  };

  const getRoleInfo = (roleId) => {
    const roles = {
      1: { name: "Super Admin", color: "bg-purple-100 text-purple-800", icon: "👑" },
      2: { name: "Admin", color: "bg-blue-100 text-blue-800", icon: "🛡️" },
      3: { name: "Block Manager", color: "bg-green-100 text-green-800", icon: "🏢" },
      4: { name: "Panchayat User", color: "bg-orange-100 text-orange-800", icon: "🏘️" }
    };
    return roles[roleId] || { name: "Unknown", color: "bg-gray-100 text-gray-800", icon: "❓" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-center">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md">
          <div className="text-red-500 text-center mb-4">
            <FaTimesCircle className="text-4xl mx-auto mb-2" />
            <p className="text-lg font-semibold">Error Loading Profile</p>
          </div>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const roleInfo = getRoleInfo(user.roleId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                  <FaUserCircle className="text-4xl text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white mb-1">
                    {user.username || "User Profile"}
                  </h1>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${roleInfo.color} bg-white/90`}>
                      {roleInfo.icon} {roleInfo.name}
                    </span>
                    {user.status === "True" ? (
                      <span className="flex items-center text-green-100 text-sm">
                        <FaCheckCircle className="mr-1" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center text-red-100 text-sm">
                        <FaTimesCircle className="mr-1" /> Inactive
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <FaEdit className="text-sm" />
                    <span>Edit Profile</span>
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSave}
                      className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <FaSave className="text-sm" />
                      <span>Save</span>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-red-500/20 hover:bg-red-500/30 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <FaTimes className="text-sm" />
                      <span>Cancel</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <FaIdBadge className="text-2xl text-green-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Personal Information</h2>
            </div>
            
            <div className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={isEditing ? editedUser.username || "" : user.username || ""}
                    onChange={(e) => setEditedUser({...editedUser, username: e.target.value})}
                    readOnly={!isEditing}
                    className={`w-full border rounded-lg p-3 transition-colors ${
                      isEditing 
                        ? "border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-200" 
                        : "border-gray-300 bg-gray-50"
                    } focus:outline-none`}
                  />
                  <FaUserCircle className="absolute right-3 top-3 text-gray-400" />
                </div>
              </div>

              {/* Email (if available) */}
              {user.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user.email}
                    readOnly
                    className="w-full border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-800 focus:outline-none"
                  />
                </div>
              )}

              {/* User ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User ID
                </label>
                <input
                  type="text"
                  value={user.userId || "N/A"}
                  readOnly
                  className="w-full border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-800 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <FaMapMarkerAlt className="text-2xl text-green-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Location & Access</h2>
            </div>
            
            <div className="space-y-4">
              {/* Block */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Block
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={blockName || "Not assigned"}
                    readOnly
                    className="w-full border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-800 focus:outline-none"
                  />
                  <FaBuilding className="absolute right-3 top-3 text-gray-400" />
                </div>
              </div>

              {/* Panchayats */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Panchayats ({panchayats.length})
                </label>
                {panchayats.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-gray-50">
                    <div className="flex flex-wrap gap-2">
                      {panchayats.map((panchayat, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                        >
                          {panchayat.name.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    value="No panchayats assigned"
                    readOnly
                    className="w-full border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-500 focus:outline-none"
                  />
                )}
              </div>

              {/* Account Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Status
                </label>
                <div className="flex items-center space-x-3">
                  {user.status === "True" ? (
                    <div className="flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                      <FaCheckCircle className="mr-2" />
                      <span className="font-medium">Active Account</span>
                    </div>
                  ) : (
                    <div className="flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-lg">
                      <FaTimesCircle className="mr-2" />
                      <span className="font-medium">Inactive Account</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">System Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Role ID</div>
              <div className="font-semibold text-gray-800">{user.roleId}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Block ID</div>
              <div className="font-semibold text-gray-800">{user.blockId || "N/A"}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Access Level</div>
              <div className="font-semibold text-gray-800">{roleInfo.name}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
